import * as React from "react";
import { useTranslation } from "next-i18next";
import Cookies from "js-cookie";

import { useAppStore } from "@/stores";
import { getCountryVersionBasedOnDomain } from "@/utils/urls";
import { endLoadingState, registerLoadingState } from "@/utils/loading";
import { v4 as uuidv4 } from "uuid";
import { useRouter, useParams, usePathname } from "next/navigation";
import { responsiveRoutes } from "@/routes";
import { LanguageEnum } from "@/services/type";

function useGeneralAppState() {
  const { i18n } = useTranslation();
  const { generalAppState, updateGeneralAppState } = useAppStore();
  const router = useRouter();
  const pathname = usePathname() || "";

  const setSSRLanguage = React.useCallback(
    (language: LanguageEnum) => {
      Cookies.set("lang", language);
      // makes sure server side component translations get updated after login
      const shouldRefresh = Object.values(responsiveRoutes).some((route) =>
        pathname.includes(route)
      );

      if (shouldRefresh) {
        router.refresh();
      }
    },
    [pathname, router]
  );

  // set job_id if not present
  const params = useParams<{ id: string }>();
  const id = params?.id || "0";

  // Extract id from url example: enior-berater-leitender-berater-997729
  const jobId = Number(id.split("-").pop());

  // set first_touch_point_url if not present
  React.useEffect(() => {
    const key = "first_touch_point_url";
    const ftpu = Cookies.get(key);

    if (!ftpu) {
      const encodedPath = window.location.href;
      Cookies.set(key, encodedPath);
    }

    // Set guestUUID cookie if not already set
    const guestUUID = Cookies.get("exp_guest_uuid");

    if (!guestUUID) {
      const generatedGuestUUID = uuidv4();
      Cookies.set("exp_guest_uuid", generatedGuestUUID);
    }
    if (jobId) {
      const fullDomain = window.location.hostname;
      const baseDomain = fullDomain.split(".").slice(-2).join(".");
      // EX: .experteer.de => to make cookie available for mobile/desktop
      Cookies.set("job_id", String(jobId), {
        domain: baseDomain,
      });
    }
  }, []);

  // set generalAppState [for all routes]
  React.useEffect(() => {
    if (generalAppState.countryVersion === undefined) {
      const countryVersion = getCountryVersionBasedOnDomain(
        window.location.href
      );

      if (countryVersion) {
        updateGeneralAppState({ countryVersion });
      }
    }
  }, [generalAppState, updateGeneralAppState]);

  // set language [should ONLY be triggered by `generalAppState.language`]
  React.useEffect(() => {
    if (
      generalAppState.language &&
      generalAppState.language !== i18n.language
    ) {
      registerLoadingState();

      // Keep server-rendered translations aligned with the resolved language.
      if (Cookies.get("lang") !== generalAppState.language) {
        setSSRLanguage(generalAppState.language);
      }

      i18n
        .changeLanguage(generalAppState.language)
        .finally(() => endLoadingState());
    }
  }, [generalAppState.language, i18n, setSSRLanguage]);
}

export default useGeneralAppState;
